import {
  types,
  flow,
} from 'mobx-state-tree';
import moment from 'moment';
import GPSLocationClient,
{ SubscribeCallbackData }
  from '../DriverLocation/GPSLocationClient';
import getAppRoot from './utils/getAppRoot';
import { IColor } from '../../types/IColor';
import { ONLINE_CHECK_FREQUENCY_MS } from '../../constants';
import { RouteDtoModelType } from './models';

interface LocationCoord {
  latitude: number,
  longitude: number,
}

export enum DriverMobileType {
  NATIVE = 'native',
  LITE = 'lite',
}

export interface RouteDriverLocation {
  routeId: string,
  routeUuid: string,
  type: DriverMobileType
  timestamp?: number,
  coords?: LocationCoord,
  color?: IColor;
  heading?: number;
  isRouteFinished?: boolean
  timeSinceLastUpdateMinutes?: number;
  driverName?: string
}

export const getRoutesLastLocation = (sortedRoutes: RouteDtoModelType[]) => {
  if (sortedRoutes.length) {
    const filteredRoutes = sortedRoutes.filter((r) => r.id && r.uuid
      && r.finished !== null
      && !r.finished);
    return filteredRoutes.map((route) => {
      return {
        lastLocation: route.lastLocation ? {
          ...route.lastLocation,
        } : undefined,
        isFinished: route.finished,
        uuid: route.uuid,
        id: route.id,
      };
    });
  }
  return [];
};

export const updateDriverLocation = (routesLastLocations: {
  lastLocation: {
    latitude: number,
    longitude: number,
    timestamp: string,
  };
  isFinished: boolean;
  uuid: string | undefined;
  id: string;
}[], updateFn: (routeId: string, payload: RouteDriverLocation) => void) => {
  if (routesLastLocations.length) {
    routesLastLocations
      .forEach((route) => {
        const payload = {
          routeId: route.id,
          routeUuid: route.uuid,
          type: DriverMobileType.LITE,
        } as unknown as RouteDriverLocation;
        if (route.lastLocation !== undefined) {
          payload.coords = { ...route.lastLocation };
          payload.timestamp = moment(route.lastLocation.timestamp).utc().valueOf();
        }
        updateFn(route.id, payload);
      });
  }
};

const getTimeSinceLastUpdateMinutes = (location: RouteDriverLocation | undefined) => {
  if (location === undefined || location.timestamp === undefined) return undefined;
  return moment().diff(moment(location.timestamp), 'minutes');
};

const DriverLocation = types.model('DriverLocation')
  .props({ loggedIn: types.optional(types.boolean, false), routeDriverLocations: types.optional(types.map(types.frozen<RouteDriverLocation>()), {}) })
  .volatile(() => ({
    // After a driver reassignment, ignore Ably rewind / stale pings older than this per route.
    locationResetAtByRouteId: {} as Record<string, number>,
  }))
  .views((self) => ({
    get driversLayerData(): RouteDriverLocation[] {
      return Array.from(self.routeDriverLocations.values())
        .filter((d) => d.coords !== undefined && d.timestamp !== undefined)
        .map((driverRoute: RouteDriverLocation) => {
          const route = getAppRoot(self).sortedRoutes.find((r) => r.id === driverRoute.routeId.toString());
          return {
            ...driverRoute, color: route?.color, isRouteFinished: route?.finished ?? false, driverName: route?.driver?.name,
          } as RouteDriverLocation;
        });
    },
  }))
  .actions((self) => ({
    updateDriverRoute(routeId: string, payload: RouteDriverLocation) {
      try {
        const resetAt = self.locationResetAtByRouteId[routeId.toString()];
        if (resetAt != null && payload.timestamp != null && payload.timestamp <= resetAt) {
          return;
        }
        if (payload.timestamp) {
          const lastUpdate = getTimeSinceLastUpdateMinutes(payload);
          payload = { ...payload, timeSinceLastUpdateMinutes: lastUpdate };
        }
        self.routeDriverLocations.set(routeId.toString(), payload);
      } catch (e) {
        console.log('Error', e);
      }
    },
    clearRouteLocation(routeId: string) {
      const key = routeId.toString();
      self.locationResetAtByRouteId[key] = Date.now();
      self.routeDriverLocations.delete(key);
    },
  }))
  .actions((self) => {
    let intervalId: ReturnType<typeof setInterval> | undefined;

    function updateAllTimesSinceLastUpdateMinutes() {
      if (self.routeDriverLocations) {
        self.routeDriverLocations.forEach((r: RouteDriverLocation) => {
          if (r.timestamp) {
            self.updateDriverRoute(r.routeId, r);
          }
        });
      }
    }

    function stopInterval() {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
    }
    function startInterval() {
      stopInterval();
      intervalId = setInterval(() => {
        updateAllTimesSinceLastUpdateMinutes();
      }, ONLINE_CHECK_FREQUENCY_MS);
    }

    return { startInterval, stopInterval };
  })
  .actions((self) => {
    const gpsLocationClient = new GPSLocationClient();

    function login(userId: string) {
      try {
        if (!self.loggedIn) {
          gpsLocationClient.setupConnection(userId);
          gpsLocationClient.connect();
          self.loggedIn = true;
        }
      } catch (error) {
        console.error('No Ably Key Detected', error);
      }
    }
    const logout = flow(function* logout() {
      try {
        yield gpsLocationClient.unsubscribeAll();
        gpsLocationClient.close();
      } catch (error) {
        console.warn('DriverLocation: Error during logout', error);
      }
      self.loggedIn = false;
    });

    const stop = flow(function* stop() {
      self.stopInterval();
      try {
        yield gpsLocationClient?.unsubscribeAll();
      } catch (error) {
        console.warn('DriverLocation: Error during stop', error);
      }
    });

    function handleGPSLocationUpdate(routeId: string, routeUuid: string) {
      return (data: SubscribeCallbackData) => self.updateDriverRoute(routeId, {
        routeId,
        routeUuid,
        type: DriverMobileType.NATIVE,
        coords: { ...data.coords },
        timestamp: data.timestamp,
        heading: data.heading,
      });
    }
    function setup(projectId: number, routeIdMap: { [id: string]: string }) {
      self.routeDriverLocations.forEach(({ routeId, routeUuid }, key) => {
        gpsLocationClient?.subscribe(
          projectId,
          routeIdMap[key],
          handleGPSLocationUpdate(routeId, routeUuid),
        );
      });
    }
    function connect(projectId: number) {
      const routeIdMap: { [id: string]: string } = {};
      self.routeDriverLocations.forEach((value, key) => { routeIdMap[key] = value.routeUuid; });

      setup(projectId, routeIdMap);
    }
    const start = flow(function* start(projectId: number) {
      yield stop();
      self.routeDriverLocations.clear();
      const routes = getAppRoot(self).routesLastLocations;
      updateDriverLocation(routes, self.updateDriverRoute);

      self.startInterval();
      connect(Number(projectId));
    });

    return {
      login,
      logout,
      start,
    };
  });

export default DriverLocation;
